/**
 * CreatorDeliveryRibbon — embedded delivery-partner ordering surface on
 * Drop pages. Matches the 7-Eleven prototype
 * (gs://wireframes-devourplay/7eleven/index.html). Renders as a horizontal
 * carousel section alongside other Drop-page content (Clips, Highlights,
 * etc.) so the viewer can order without leaving the Drop.
 *
 * V1 scope: visual-only. Hardcoded 7NOW product set; ADD buttons are
 * no-ops; footer "ORDER ON 7NOW" CTA opens 7now.com in a new tab. Real
 * cart + ordering integration ships as a separate phase.
 *
 * V2 scope: per-creator partner selection (7NOW / DoorDash / UberEats)
 * driven by a `Drop.deliveryPartner` field + admin/creator-portal config.
 * The component already accepts a `partner` prop so the V2 work is just
 * wiring data through.
 */

import {ReactElement} from "react";
import {ExternalLink} from "@/widgets/ExternalLink";
import "./CreatorDeliveryRibbon.scss";

type DeliveryPartnerKey = "7NOW" | "DOORDASH" | "UBEREATS";

interface DeliveryPartnerConfig {
    /** Display name in the badge pill. */
    badgeLabel: string;
    /** Title shown next to the badge. */
    title: string;
    /** Subtitle copy under the title. */
    subtitle: string;
    /** Order CTA copy in the footer. */
    ctaLabel: string;
    /** Where the footer CTA points. */
    ctaUrl: string;
    /** Estimated delivery time chip on the right. */
    deliveryEta: string;
    /** XP-bonus notice copy. */
    xpNoticeBefore: string;
    xpNoticeBold: string;
    xpNoticeAfter: string;
    /** Brand accent for the badge background. */
    badgeColor: string;
}

const PARTNER_CONFIGS: Record<DeliveryPartnerKey, DeliveryPartnerConfig> = {
    "7NOW": {
        badgeLabel: "7NOW",
        title: "Fuel Your Session",
        subtitle: "Order 7-Eleven delivery without leaving the hub",
        ctaLabel: "ORDER ON 7NOW → EARN 2× XP",
        ctaUrl: "https://7now.7-eleven.com/",
        deliveryEta: "~28 min delivery",
        xpNoticeBefore: "Hub members earn ",
        xpNoticeBold: "2× XP on every 7NOW order",
        xpNoticeAfter: " placed from this hub — plus every order counts toward the 7NOW Mass Race leaderboard.",
        badgeColor: "#0f8e3e",
    },
    /*
     * DOORDASH + UBEREATS: placeholder configs reserved for V2 per-creator
     * partner selection. None of the copy, ETA, or CTA wording has been
     * approved by the respective brand teams — do NOT surface either
     * partner in production until brand sign-off lands. Today only the
     * "7NOW" config is approved for the 7-Eleven prototype demo.
     */
    "DOORDASH": {
        badgeLabel: "DoorDash",
        title: "Fuel Your Session",
        subtitle: "Order from local restaurants without leaving the hub",
        ctaLabel: "ORDER ON DOORDASH → EARN 2× XP",
        ctaUrl: "https://www.doordash.com/",
        deliveryEta: "~35 min delivery",
        xpNoticeBefore: "Hub members earn ",
        xpNoticeBold: "2× XP on every DoorDash order",
        xpNoticeAfter: " placed from this hub.",
        badgeColor: "#eb1700",
    },
    "UBEREATS": {
        badgeLabel: "Uber Eats",
        title: "Fuel Your Session",
        subtitle: "Order local delivery without leaving the hub",
        ctaLabel: "ORDER ON UBER EATS → EARN 2× XP",
        ctaUrl: "https://www.ubereats.com/",
        deliveryEta: "~30 min delivery",
        xpNoticeBefore: "Hub members earn ",
        xpNoticeBold: "2× XP on every Uber Eats order",
        xpNoticeAfter: " placed from this hub.",
        badgeColor: "#06c167",
    },
};

interface ProductItem {
    id: string;
    name: string;
    priceUsd: number;
    xpBonus: number;
    /** Optional real product image URL (CDN-hosted). Falls back to a clean gradient placeholder when absent. */
    imageUrl?: string;
}

/*
 * Static product set matching the 7NOW prototype.
 *
 * Image source: `storage.googleapis.com/wireframes-devourplay/7eleven/assets/`
 * is the design-prototype GCS bucket (public-read). These URLs are SAFE
 * for the partnership demo on develop mirrors but MUST be migrated to a
 * production-controlled CDN before the integration goes live — the
 * wireframes bucket is not under release management and could change
 * ACLs or path layout without warning.
 *
 * When per-partner ordering is wired up the products will come from a
 * backend service (partner catalog API).
 */
const SEVEN_NOW_PRODUCTS: ProductItem[] = [
    {id: "slurpee", name: "Slurpee — Any Size", priceUsd: 1.99, xpBonus: 200, imageUrl: "https://storage.googleapis.com/wireframes-devourplay/7eleven/assets/711-slurpee-cherry.webp"},
    {id: "big-gulp", name: "Big Gulp — Any Size", priceUsd: 1.49, xpBonus: 100, imageUrl: "https://storage.googleapis.com/wireframes-devourplay/7eleven/assets/711-biggulp.webp"},
    {id: "big-bite", name: "Big Bite Hot Dog", priceUsd: 2.99, xpBonus: 100, imageUrl: "https://storage.googleapis.com/wireframes-devourplay/7eleven/assets/711-hotdog.webp"},
    {id: "wings", name: "Chicken Wings 5pc", priceUsd: 6.00, xpBonus: 150, imageUrl: "https://storage.googleapis.com/wireframes-devourplay/7eleven/assets/711-wings.webp"},
    {id: "pizza", name: "Large Pepperoni Pizza", priceUsd: 9.99, xpBonus: 150, imageUrl: "https://storage.googleapis.com/wireframes-devourplay/7eleven/assets/711-pizza.webp"},
    {id: "doritos", name: "Doritos Nacho Cheese", priceUsd: 2.49, xpBonus: 75, imageUrl: "https://storage.googleapis.com/wireframes-devourplay/7eleven/assets/711-doritos.webp"},
    {id: "mms", name: "M&M's Share Size", priceUsd: 3.29, xpBonus: 75, imageUrl: "https://storage.googleapis.com/wireframes-devourplay/7eleven/assets/711-mms.webp"},
];

interface CreatorDeliveryRibbonProps {
    /** Which delivery partner is wired to this Drop. Defaults to 7NOW for the prototype demo. */
    partner?: DeliveryPartnerKey;
    /** When true, the "account linked" footer indicator is shown. */
    accountLinked?: boolean;
}

function formatPrice(n: number): string {
    return `$${n.toFixed(2)}`;
}

export function CreatorDeliveryRibbon({
    partner: _partner = "7NOW",
    accountLinked = true,
}: CreatorDeliveryRibbonProps): ReactElement | null {
    const config = PARTNER_CONFIGS[_partner];
    const products = SEVEN_NOW_PRODUCTS;
    /*
     * Footer CTA: opens the partner's site in a new tab. When the real
     * cart integration lands this should fire a "compose cart" intent
     * that pre-populates the partner's checkout with any items the user
     * has tapped ADD on. For V1 it's a single passthrough link.
     */

    return (
        <section className="delivery-ribbon" aria-label={`Order via ${config.badgeLabel}`}>
            {/*
              * SVG filter chain that knocks out white backgrounds from the
              * product .webp images.
              *
              * Step 1 (feColorMatrix) — computes alpha as `-R - G - B + 3*A`.
              *   Pure white (R=G=B=1, A=1) → A=0 (fully transparent).
              *   Colored pixels → alpha clamps to 1 (opaque).
              * Step 2 (feComponentTransfer / feFuncA linear) — boosts the
              *   resulting alpha so any pixel that wasn't pure-white
              *   snaps back to full opacity. Without this, products with
              *   bright highlights (Doritos bag orange, M&M's wrapper)
              *   come out ghosted because their highlight pixels are
              *   close-to-white. slope=3, intercept=-0.3 means:
              *     A=0   → clamps to 0  (pure-white pixels stay transparent)
              *     A=0.2 → 0.3 (translucent — antialiased edges, fine)
              *     A=0.4 → 0.9 (mostly opaque)
              *     A≥0.5 → clamps to 1  (full opacity restored)
              *
              * The filter is referenced by every product `<img>` via
              * `filter: url(#delivery-ribbon-knockout-white)` in SCSS.
              */}
            <svg className="delivery-ribbon__svg-defs" aria-hidden="true" focusable="false">
                <defs>
                    <filter id="delivery-ribbon-knockout-white">
                        <feColorMatrix
                            type="matrix"
                            values="1 0 0 0 0
                                    0 1 0 0 0
                                    0 0 1 0 0
                                    -1 -1 -1 3 0"
                        />
                        <feComponentTransfer>
                            <feFuncA type="linear" slope="3" intercept="-0.3" />
                        </feComponentTransfer>
                    </filter>
                </defs>
            </svg>

            <div className="delivery-ribbon__header">
                <span className="delivery-ribbon__badge" style={{backgroundColor: config.badgeColor}}>
                    {config.badgeLabel}
                </span>
                <div className="delivery-ribbon__copy">
                    <div className="delivery-ribbon__title">{config.title}</div>
                    <div className="delivery-ribbon__subtitle">{config.subtitle}</div>
                </div>
                <span className="delivery-ribbon__eta">{config.deliveryEta}</span>
            </div>

            <div className="delivery-ribbon__xp-notice">
                <span className="delivery-ribbon__xp-spark">✦</span>
                <span>
                    {config.xpNoticeBefore}
                    <strong>{config.xpNoticeBold}</strong>
                    {config.xpNoticeAfter}
                </span>
            </div>

            <div className="delivery-ribbon__cards" role="list">
                {products.map(p =>
                    <article key={p.id} className="delivery-ribbon__card" role="listitem">
                        <span className="delivery-ribbon__card-xp">+{p.xpBonus} XP</span>
                        <div className="delivery-ribbon__card-image" aria-hidden="true">
                            {p.imageUrl &&
                                <img src={p.imageUrl} alt="" loading="lazy" referrerPolicy="no-referrer" />
                            }
                        </div>
                        <div className="delivery-ribbon__card-name">{p.name}</div>
                        <div className="delivery-ribbon__card-price">{formatPrice(p.priceUsd)}</div>
                        <ExternalLink
                            className="delivery-ribbon__card-add"
                            aria-label={`Add ${p.name} to order`}
                            href={config.ctaUrl}
                        >
                            ADD
                        </ExternalLink>
                    </article>)}
            </div>

            <ExternalLink className="delivery-ribbon__cta" href={config.ctaUrl}>
                {config.ctaLabel}
            </ExternalLink>

            {accountLinked &&
                <div className="delivery-ribbon__linked">{config.badgeLabel} account linked ✓</div>
            }
        </section>
    );
}
